import { useState } from 'react';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import Stack from '@mui/material/Stack';
import { Grid } from '@mui/material';
import { Box } from '@mui/system';
import FormHelperText from '@mui/material/FormHelperText';
import LoadingSpinner from '../../../support/LoadingSpinner';
import toast from 'react-hot-toast';
import axios from '../../../../services/ApiService';

export function AddAdminUser(): React.JSX.Element {
  const [loading, setLoading] = useState(false);
  const [formValues, setFormValues] = useState({
    firstName: '',
    lastName: '',
    email: '',
    contact: ''
  });

  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
    contact: ''
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormValues(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const newErrors = {
      firstName: '',
      lastName: '',
      email: '',
      contact: ''
    };

    if (!formValues.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formValues.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formValues.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formValues.email)) {
      newErrors.email = 'Invalid email address';
    }

    if (!formValues.contact.trim()) {
      newErrors.contact = 'Phone number is required';
    } else if (formValues.contact.trim()) {
      const phoneRegex = /^[0-9\s\-\.\(\)\+]+$/;
      if (!phoneRegex.test(formValues.contact.trim())) {
        newErrors.contact = 'Please enter a valid phone number';
      }
    }

    if (Object.values(newErrors).some(error => error !== '')) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post("/createAdmin", formValues);
      console.log('Admin created successfully ' + response.data);
      toast.success("New Admin User Created Successfully.");
      
      setFormValues({
        firstName: '',
        lastName: '',
        email: '',
        contact: ''
      });
      
    } catch (error) {
      console.error('Error creating admin user.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Card>
        <CardHeader subheader="Add New Admin User" title="Add Admin" />
        <Divider />
        <CardContent>
          <Box sx={{ maxWidth: '800px' }}>
            <Stack spacing={3} sx={{ maxWidth: 'flex' }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth required error={!!errors.firstName}>
                    <InputLabel>First Name</InputLabel>
                    <OutlinedInput 
                      label="First Name" 
                      name="firstName" 
                      type="text"
                      value={formValues.firstName}
                      onChange={handleChange}
                      inputProps={{ maxLength: 40 }}
                    />
                    {errors.firstName && (
                      <FormHelperText error>{errors.firstName}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth required error={!!errors.lastName}>
                    <InputLabel>Last Name</InputLabel>
                    <OutlinedInput 
                      label="Last Name" 
                      name="lastName" 
                      type="text"
                      value={formValues.lastName}
                      onChange={handleChange}
                      inputProps={{ maxLength: 40 }}
                    />
                    {errors.lastName && (
                      <FormHelperText error>{errors.lastName}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth required error={!!errors.email}>
                    <InputLabel>Email</InputLabel>
                    <OutlinedInput 
                      label="Email" 
                      name="email" 
                      type="email"
                      value={formValues.email}
                      onChange={handleChange}
                      inputProps={{ maxLength: 40 }}
                    />
                    {errors.email && (
                      <FormHelperText error>{errors.email}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth required error={!!errors.contact}>
                    <InputLabel>Phone number</InputLabel>
                    <OutlinedInput 
                      label="Phone number" 
                      name="contact"
                      type="tel"
                      value={formValues.contact}
                      onChange={handleChange}
                      inputProps={{ 
                        maxLength: 15,
                        pattern: '[0-9]*' // Only allow numbers
                      }}
                    />
                    {errors.contact && (
                      <FormHelperText error>{errors.contact}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              </Grid>
            </Stack>
          </Box>
        </CardContent>
        <Divider />
        <CardActions sx={{ justifyContent: 'flex-end' }}>
          <Button variant="contained" type="submit">Create</Button>
        </CardActions>
      </Card>
      <LoadingSpinner loading={loading} />
    </form>
  );
}
